import { useMemo } from 'react'
import { useEsri } from '../../esriHooks'

import { LOCATOR_URL } from '../../map/constants'
import { RESULT_TYPES } from '../constants'

export const useLocator = () => {
  const [ready, { Locator }] = useEsri(['esri/tasks/Locator'])

  const locator = useMemo(() => {
    if (ready && Locator) {
      const l = new Locator({
        url: LOCATOR_URL
      })
      return l
    }
    return null
  }, [ready, Locator])

  return locator
}

export const searchAddresses = async (locator, Point, query, callback) => {
  const candidates = await locator.addressToLocations({
    address: {
      SingleLine: query,
      outSR: 4326
    },
    outFields: "*"
  })
  const results = processCandidates(candidates)
  const formattedResults = formatAddressSearchResults(Point, results)
  if (formattedResults && formattedResults.results.length) callback(formattedResults)
}

const formatAddressSearchResults = (Point, results) => {
  return {
    id: RESULT_TYPES.ADDRESS,
    title: 'Address',
    showResults: false,
    results: results.map(r => ({
      label: r.address,
      geometry: new Point(r.location)
    }))
  }
}

const candidate_isPointAddress = (candidate) => candidate.attributes.Addr_type === 'PointAddress'

const candidate_compare = (a, b) => {
  if (a.score === b.score) {
    if (candidate_isPointAddress(a)) return 1
    if (candidate_isPointAddress(b)) return -1
    return 0
  }
  return a.score > b.score ? -1 : 1
}

const candidate_isEqual = (a, b) => {
  return (
    a.attributes.StreetName === b.attributes.StreetName &&
    a.attributes.City === b.attributes.City &&
    a.attributes.SufType === b.attributes.SufType &&
    a.attributes.PreType === b.attributes.PreType &&
    a.attributes.ZIP === b.attributes.ZIP
  )
}

const MIN_SCORE = 80
const processCandidates = (candidates) => {
  // If at least one result is better than the min score, filter by the min score
  if (candidates.some(c => c.score >= MIN_SCORE))
    candidates = candidates.filter(c => c.score >= MIN_SCORE)

  // Remove duplicate Point/Street Addresses
  candidates.sort(candidate_compare)
  const results = candidates.filter(candidate_isPointAddress)
  candidates.forEach(candidate => {
    if (candidate_isPointAddress(candidate)) return
    const alreadyInResults = results.some(result => candidate_isEqual(candidate, result))
    if (!alreadyInResults) {
      results.push(candidate)
    }
  })
  // Sort Results
  results.sort(candidate_compare)
  return results
}